// Dữ liệu minh hoạ cho Dashboard Telesales: sinh cố định theo từng ngày (cùng ngày → cùng số liệu).
// Khi có API: thay buildModel bằng dữ liệu thật (khách, cuộc gọi, lịch hẹn) — logic.ts giữ nguyên.
import dayjs from 'dayjs';
import type { Mang } from '../components/Types';

export const MIN = 60_000;
export const HOUR = 60 * MIN;
export const DAY = 24 * HOUR;
export const OPEN_H = 8;
export const CLOSE_H = 21;

export const MANGS: Mang[] = ['Newhair', 'Clinic', 'PTTM', 'Partnership'];

export interface Branch { id: string; name: string }
export const BRANCHES: Branch[] = [
  { id: 'q1', name: 'Trần Hưng Đạo Q1' },
];

export interface Agent { id: string; name: string; mang: Mang | null; color: string }
/** Nhân viên Telesales — mang = null: chưa phân luồng, nhận khách tràn của mọi mảng */
export const AGENTS: Agent[] = [
  { id: 'NV-01', name: 'Nguyễn Thu Hà', mang: 'Newhair', color: '#3e91ab' },
  { id: 'NV-02', name: 'Trần Minh Khoa', mang: 'Clinic', color: '#6366f1' },
  { id: 'NV-03', name: 'Lê Ngọc Anh', mang: 'Newhair', color: '#0ea5e9' },
  { id: 'NV-04', name: 'Phạm Quốc Bảo', mang: 'PTTM', color: '#f59e0b' },
  { id: 'NV-05', name: 'Võ Thảo My', mang: null, color: '#ec4899' },
  { id: 'NV-06', name: 'Đặng Gia Huy', mang: null, color: '#10b981' },
];

export interface Consultant { id: string; name: string; branchId: string; color: string }
/** Tư vấn viên (sale) tại cơ sở — tiếp khách đến theo lịch Telesales đặt */
export const CONSULTANTS: Consultant[] = [
  { id: 'TV-11', name: 'Hoàng Mai Linh', branchId: 'q1', color: '#8b5cf6' },
  { id: 'TV-12', name: 'Bùi Đức Tâm', branchId: 'q1', color: '#14b8a6' },
  { id: 'TV-21', name: 'Ngô Thanh Vy', branchId: 'q1', color: '#f97316' },
  { id: 'TV-22', name: 'Lý Hoàng Nam', branchId: 'q1', color: '#22c55e' },
  { id: 'TV-31', name: 'Đỗ Khánh Ngân', branchId: 'q1', color: '#e11d48' },
  { id: 'TV-32', name: 'Trương Minh Trí', branchId: 'q1', color: '#0284c7' },
];

export const SOURCES = ['Facebook', 'TikTok', 'Zalo', 'Website', 'Giới thiệu', 'Hotline'] as const;
export type Source = (typeof SOURCES)[number];

/** Mã trạng thái khách (S) — CS: đã chốt, chuyển CSKH */
export type Stage = 'S0' | 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S5.1' | 'S6' | 'S6.1' | 'S6.2' | 'CS';

export interface Lead {
  id: string;
  name: string;
  phone: string;
  mang: Mang;
  source: Source;
  branchId: string;
  ownerId: string;
  createdAt: number;
  /** Lần đầu nói chuyện được với khách */
  connectAt?: number;
  /** Mã S sau khi kết nối (khi chưa có lịch hẹn) */
  stage0: Stage;
  callbackAt?: number;
  apptId?: string;
  /** Bị bỏ sót khỏi lịch gọi chăm sóc (mô phỏng khách bị bỏ quên) */
  neglect?: boolean;
}

export type CallKind = 'first' | 'callback' | 'care' | 'confirm' | 'inbound' | 'missedback';
export interface Call {
  id: string;
  at: number;
  dir: 'in' | 'out';
  kind: CallKind;
  phone: string;
  leadId?: string;
  agentId: string;
  waitSec: number;
  talkSec: number;
  acwSec: number;
  /** Gọi đến: nhân viên nhận máy · Gọi đi: khách nghe máy */
  ok: boolean;
}

export type ApptStatus = 'booked' | 'confirmed' | 'arrived' | 'consulting' | 'won' | 'deposit' | 'no_need' | 'medical' | 'noshow' | 'cancelled';
export type Fate = Extract<ApptStatus, 'won' | 'deposit' | 'no_need' | 'medical' | 'noshow' | 'cancelled'>;

export interface Appt {
  id: string;
  leadId: string;
  branchId: string;
  at: number;
  kind: 'Tư vấn' | 'Thực hiện';
  bookedBy: string;
  bookedAt: number;
  note: string;
  /** Kết cục đã định sẵn của lịch (chỉ dùng để mô phỏng các lịch đã qua) */
  fate: Fate;
  confirmed: boolean;
  consultantId: string;
  amount: number;
}

export interface Model {
  dayStart: number;
  leads: Lead[];
  leadById: Record<string, Lead>;
  calls: Call[];
  appts: Appt[];
  apptById: Record<string, Appt>;
}

/* ---------- sinh ngẫu nhiên có hạt giống ---------- */
const hash = (s: string) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};
export const rng = (seed: string) => {
  let a = hash(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
type Rnd = () => number;
const int = (r: Rnd, a: number, b: number) => a + Math.floor(r() * (b - a + 1));
const pick = <T,>(r: Rnd, arr: readonly T[]) => arr[Math.floor(r() * arr.length)];
const weighted = <T,>(r: Rnd, items: [T, number][]) => {
  let x = r() * items.reduce((s, [, w]) => s + w, 0);
  for (const [v, w] of items) { x -= w; if (x <= 0) return v; }
  return items[items.length - 1][0];
};

/** Giờ trong ngày có trọng số (cao điểm trưa và tối) */
const HOUR_W: [number, number][] = [[8, 3], [9, 6], [10, 8], [11, 10], [12, 11], [13, 8], [14, 6], [15, 6], [16, 7], [17, 7], [18, 8], [19, 10], [20, 7]];
const atHour = (r: Rnd, day: number) => day + weighted(r, HOUR_W) * HOUR + int(r, 0, 59) * MIN + int(r, 0, 59) * 1000;

/** Đẩy mốc thời gian vào giờ làm việc */
const working = (r: Rnd, t: number) => {
  const d = dayjs(t);
  if (d.hour() >= CLOSE_H) return d.add(1, 'day').startOf('day').valueOf() + OPEN_H * HOUR + int(r, 5, 90) * MIN;
  if (d.hour() < OPEN_H) return d.startOf('day').valueOf() + OPEN_H * HOUR + int(r, 5, 60) * MIN;
  return t;
};

const OWNERS: Record<Mang, string[]> = {
  Newhair: ['NV-01', 'NV-03', 'NV-03', 'NV-05'],
  Clinic: ['NV-02', 'NV-02', 'NV-06'],
  PTTM: ['NV-04', 'NV-04', 'NV-05'],
  Partnership: ['NV-06'],
};
const NOTES: Record<Mang, string[]> = {
  Newhair: ['Rụng tóc vùng đỉnh, muốn soi da đầu', 'Hói chữ M, hỏi cấy tóc', 'Tóc thưa sau sinh, đã gửi báo giá Zalo', 'Muốn làm AI Hair Analysis trước'],
  Clinic: ['Nám má, đã gửi báo giá K·Reskin', 'Mụn ẩn lâu năm, muốn khám da', 'Hỏi phác đồ K·Lift trẻ hoá', 'Sẹo rỗ, cần bác sĩ đánh giá'],
  PTTM: ['Nâng mũi, cần bác sĩ tư vấn trực tiếp', 'Cắt mí, hỏi thời gian hồi phục', 'Gói theo độ tuổi 40+, đi cùng người nhà'],
  Partnership: ['Đối tác giới thiệu — combo chăm sóc da', 'Khách doanh nghiệp, cần báo giá theo nhóm'],
};
const PRICE: Record<Mang, [number, number]> = { Newhair: [25, 80], Clinic: [5, 30], PTTM: [30, 120], Partnership: [10, 40] };
const LAST = ['A', 'B', 'C', 'D', 'G', 'H', 'K', 'L', 'M', 'N', 'P', 'Q', 'T', 'V', 'X', 'Đ'];

/** Số ngày lùi lại để sinh khách / cuộc gọi */
export const LEAD_DAYS = 56;
export const CALL_DAYS = 35;

const cache: Record<string, Model> = {};

/** Toàn bộ dữ liệu quanh ngày `dateKey` (YYYY-MM-DD) — kể cả sự kiện sau giờ hiện tại, logic tự lọc theo `now` */
export function buildModel(dateKey: string): Model {
  if (cache[dateKey]) return cache[dateKey];
  const today = dayjs(dateKey).startOf('day');
  const leads: Lead[] = [];
  const calls: Call[] = [];
  const appts: Appt[] = [];
  const callFrom = today.subtract(CALL_DAYS, 'day').valueOf();
  let seq = 0;
  const careAt: Record<string, number> = {};
  const addCall = (c: Omit<Call, 'id'>) => { if (c.at >= callFrom) calls.push({ ...c, id: `C${++seq}` }); };
  const talk = (r: Rnd, ok: boolean) => (ok ? int(r, 50, 560) : 0);
  const acw = (r: Rnd) => (r() < 0.06 ? int(r, 330, 520) : int(r, 15, 200));

  for (let back = LEAD_DAYS; back >= 0; back--) {
    const day = today.subtract(back, 'day');
    const ds = day.valueOf();
    const key = day.format('YYYYMMDD');
    const r = rng(`tele-${key}`);
    const weekend = day.day() === 0 || day.day() === 6;

    /* Khách mới trong ngày và hành trình gọi của từng khách */
    const n = weekend ? int(r, 24, 32) : int(r, 38, 52);
    for (let i = 0; i < n; i++) {
      const mang = weighted<Mang>(r, [['Newhair', 40], ['Clinic', 30], ['PTTM', 22], ['Partnership', 8]]);
      const source = weighted<Source>(r, [['Facebook', 34], ['TikTok', 24], ['Zalo', 14], ['Website', 10], ['Giới thiệu', 8], ['Hotline', 10]]);
      const ownerId = pick(r, OWNERS[mang]);
      const createdAt = atHour(r, ds);
      const lead: Lead = {
        id: `KH-${key.slice(2)}${String(i + 1).padStart(2, '0')}`,
        name: `${r() < 0.68 ? 'Chị' : 'Anh'} ${pick(r, LAST)}.${pick(r, LAST)}.${pick(r, LAST)}`,
        phone: `0${pick(r, ['90', '91', '93', '96', '97', '98', '94'])}***${String(int(r, 0, 9999)).padStart(4, '0')}`,
        mang, source, ownerId, createdAt,
        branchId: 'q1',
        stage0: 'S0',
        neglect: r() < 0.02,
      };
      leads.push(lead);

      // Gọi lần đầu (khách Hotline tự gọi đến nên đã kết nối ngay)
      let t = createdAt;
      if (source === 'Hotline') {
        addCall({ at: createdAt, dir: 'in', kind: 'inbound', phone: lead.phone, leadId: lead.id, agentId: ownerId, waitSec: int(r, 3, 25), talkSec: talk(r, true), acwSec: acw(r), ok: true });
        lead.connectAt = createdAt;
      } else {
        t = working(r, createdAt + weighted(r, [[int(r, 2, 14), 68], [int(r, 15, 45), 22], [int(r, 46, 180), 10]]) * MIN);
        for (let a = 0; a < 4; a++) {
          const ok = r() < 0.62;
          addCall({ at: t, dir: 'out', kind: 'first', phone: lead.phone, leadId: lead.id, agentId: ownerId, waitSec: 0, talkSec: talk(r, ok), acwSec: acw(r), ok });
          if (ok) { lead.connectAt = t; break; }
          t = working(r, t + int(r, 30, 240) * MIN);
        }
      }
      if (!lead.connectAt) continue;

      const outcome = weighted(r, [['book', 40], ['S1', 11], ['S2', 18], ['S3', 16], ['S4', 15]]);
      if (outcome === 'book') {
        lead.stage0 = 'S5';
        const sameDay = r() < 0.12 && dayjs(lead.connectAt).hour() < 15;
        const at = sameDay
          ? dayjs(lead.connectAt).add(int(r, 2, 4), 'hour').minute(0).second(0).millisecond(0).valueOf()
          : dayjs(lead.connectAt).startOf('day').add(int(r, 1, 5), 'day').hour(int(r, 9, 19)).minute(r() < 0.5 ? 0 : 30).valueOf();
        const fate = weighted<Fate>(r, [['won', 30], ['deposit', 13], ['no_need', 20], ['medical', 7], ['noshow', 24], ['cancelled', 6]]);
        const [lo, hi] = PRICE[mang];
        const branchCons = CONSULTANTS.filter(c => c.branchId === lead.branchId);
        const appt: Appt = {
          id: `LH-${lead.id.slice(3)}`, leadId: lead.id, branchId: lead.branchId, at,
          kind: r() < 0.86 ? 'Tư vấn' : 'Thực hiện', bookedBy: ownerId, bookedAt: lead.connectAt,
          note: pick(r, NOTES[mang]), fate, confirmed: r() < 0.7, consultantId: pick(r, branchCons).id,
          amount: fate === 'won' ? int(r, lo * 2, hi * 2) * 500_000 : fate === 'deposit' ? int(r, 4, 20) * 500_000 : 0,
        };
        appts.push(appt);
        lead.apptId = appt.id;
        // Gọi xác nhận lịch hôm trước
        if (appt.confirmed && !sameDay) {
          const c = dayjs(at).subtract(1, 'day').startOf('day').valueOf() + int(r, 9, 17) * HOUR + int(r, 0, 59) * MIN;
          if (c > appt.bookedAt) addCall({ at: c, dir: 'out', kind: 'confirm', phone: lead.phone, leadId: lead.id, agentId: ownerId, waitSec: 0, talkSec: int(r, 40, 150), acwSec: acw(r), ok: true });
        }
      } else {
        lead.stage0 = outcome as Stage;
        const cb = dayjs(lead.connectAt).startOf('day').add(int(r, 1, 4), 'day').valueOf() + int(r, 9, 19) * HOUR + (r() < 0.5 ? 0 : 30) * MIN;
        lead.callbackAt = cb;
        // 85% nhân viên gọi lại đúng hẹn; số còn lại thành việc trễ
        if (r() < 0.85) {
          const ok = r() < 0.7;
          addCall({ at: cb + int(r, 0, 25) * MIN, dir: 'out', kind: 'callback', phone: lead.phone, leadId: lead.id, agentId: ownerId, waitSec: 0, talkSec: talk(r, ok), acwSec: acw(r), ok });
        }
      }
    }

    if (ds < callFrom) continue;
    const recent = leads.filter(l => l.createdAt < ds && l.createdAt >= ds - 35 * DAY);

    /* Cuộc gọi đến trong ngày */
    const inbound = weekend ? int(r, 22, 32) : int(r, 36, 54);
    for (let i = 0; i < inbound; i++) {
      const at = atHour(r, ds);
      const lead = r() < 0.7 && recent.length ? pick(r, recent) : undefined;
      const h = dayjs(at).hour();
      const missed = r() < ((h >= 11 && h <= 13) || h >= 19 ? 0.17 : 0.07);
      const agentId = lead?.ownerId ?? pick(r, AGENTS).id;
      const phone = lead?.phone ?? `0${pick(r, ['86', '88', '35', '39', '70'])}***${String(int(r, 0, 9999)).padStart(4, '0')}`;
      addCall({ at, dir: 'in', kind: 'inbound', phone, leadId: lead?.id, agentId, waitSec: missed ? int(r, 25, 120) : Math.min(110, 3 + Math.round(-Math.log(1 - r()) * 15)), talkSec: missed ? 0 : talk(r, true), acwSec: missed ? 0 : acw(r), ok: !missed });
      if (missed && r() < 0.78) {
        const ok = r() < 0.75;
        addCall({ at: working(r, at + int(r, 4, 80) * MIN), dir: 'out', kind: 'missedback', phone, leadId: lead?.id, agentId, waitSec: 0, talkSec: talk(r, ok), acwSec: acw(r), ok });
      }
    }

    /* Gọi chăm sóc khách đang theo dõi (S2–S4) */
    for (const ag of AGENTS) {
      // Ưu tiên khách lâu chưa được chạm nhất
      const mine = recent
        .filter(l => l.ownerId === ag.id && l.connectAt && l.connectAt < ds && !l.apptId && !l.neglect && ['S2', 'S3', 'S4'].includes(l.stage0))
        .sort((a, b) => (careAt[a.id] ?? a.connectAt ?? 0) - (careAt[b.id] ?? b.connectAt ?? 0));
      if (!mine.length) continue;
      const k = weekend ? int(r, 14, 22) : int(r, 26, 40);
      for (let i = 0; i < k; i++) {
        const lead = mine[i % mine.length];
        careAt[lead.id] = ds;
        const ok = r() < 0.55;
        addCall({ at: ds + int(r, OPEN_H * 60 + 20, (CLOSE_H - 1) * 60 + 30) * MIN, dir: 'out', kind: 'care', phone: lead.phone, leadId: lead.id, agentId: ag.id, waitSec: 0, talkSec: talk(r, ok), acwSec: acw(r), ok });
      }
    }
  }

  calls.sort((a, b) => a.at - b.at);
  appts.sort((a, b) => a.at - b.at);
  const model: Model = {
    dayStart: today.valueOf(),
    leads,
    leadById: Object.fromEntries(leads.map(l => [l.id, l])),
    calls,
    appts,
    apptById: Object.fromEntries(appts.map(a => [a.id, a])),
  };
  cache[dateKey] = model;
  return model;
}

export const agentOf = (id?: string) => AGENTS.find(a => a.id === id);
export const consultantOf = (id?: string) => CONSULTANTS.find(c => c.id === id);
export const branchOf = (id?: string) => BRANCHES.find(b => b.id === id);
